import React from 'react';
import { useParams, Link } from 'react-router-dom';
import styles from '../styles/program.module.css';
import PriceCalculator from 'components/PriceCaluculator';

const formatDescription = description => {
  const sections = description.split('\n');
  return sections.map((section, index) => {
    if (section.includes('Key Topics:')) {
      const [title, ...points] = section.split('\n');
      return (
        <div key={index} className={styles.descriptionSection}>
          <h3 className={styles.sectionTitle} data-section="key-topics">
            {title.trim()}
          </h3>
          <ul className={styles.bulletPoints}>
            {points.map((point, i) => (
              <li key={i}>{point.replace('•', '').trim()}</li>
            ))}
          </ul>
        </div>
      );
    } else if (section.includes('Learning Outcomes:')) {
      const [title, ...content] = section.split('\n');
      return (
        <div key={index} className={styles.descriptionSection}>
          <h3 className={styles.sectionTitle} data-section="Learning Outcomes">
            {'🎓  ' + title.trim()}
          </h3>
          <div className={styles.weeklyBreakdown}>
            {content.map((line, i) => {
              if (line.trim()) {
                const [week, description] = line.split(':').map(s => s.trim());
                return (
                  <div key={i} className={styles.weekItem}>
                    <div className={styles.weekTitle}>{week}</div>
                    {description && <div className={styles.weekContent}>{description}</div>}
                  </div>
                );
              }
              return null;
            })}
          </div>
        </div>
      );
    } else {
      return (
        <p key={index} className={styles.descriptionParagraph}>
          {section}
        </p>
      );
    }
  });
};

export default function Program() {
  const { id } = useParams();

  const programs = [
    {
      id: 1,
      title: 'Frontend Development',
      description: 'Master modern frontend development with HTML, CSS, JavaScript, and React.',
      fullDescription: `4-Week Intensive Frontend Development Program covering HTML, CSS, JavaScript, and React.
  
  Key Topics:
  • HTML5 semantics & accessibility
  • Modern CSS (Flexbox, Grid, Animations)
  • JavaScript ES6+ & DOM manipulation
  • React.js with hooks and state management
  • Responsive design & performance optimization
  • Testing with Jest & React Testing Library
  • Modern development tools and practices
  `,
      duration: '4 weeks',
      schedule: '30 hours/week',
      prerequisites: ['Basic computer knowledge', 'Understanding of web basics'],
      price: '₹1399',
      image: 'https://images.unsplash.com/photo-1547658719-da2b51169166?w=800',
      curriculum: [
        {
          week: 'Week 1',
          content: 'HTML & CSS Fundamentals, Modern Layouts',
        },
        {
          week: 'Week 2',
          content: 'JavaScript Fundamentals & DOM Manipulation',
        },
        {
          week: 'Week 3',
          content: 'React Fundamentals, Hooks & Routing',
        },
        {
          week: 'Week 4',
          content: 'Advanced React, Testing & Final Project',
        },
      ],
    },
    {
      id: 2,
      title: 'Backend Development',
      description: 'Master Node.js, Express.js, MongoDB, and backend architecture.',
      fullDescription: `4-Week Intensive Backend Development Program focusing on Node.js, Express.js, and MongoDB.
  
  Key Topics:
  • Node.js fundamentals & architecture
  • Express.js & RESTful API design
  • MongoDB & Mongoose ODM
  • Authentication & Authorization
  • API security & testing
  • Deployment & DevOps basics
  • Performance optimization`,
      duration: '4 weeks',
      schedule: '30 hours/week',
      prerequisites: [
        'JavaScript fundamentals',
        'Basic programming concepts',
        'Command line familiarity',
      ],
      price: '₹1599',
      image: 'https://images.unsplash.com/photo-1623479322729-28b25c16b011?w=800',
      curriculum: [
        {
          week: 'Week 1',
          content: 'Node.js Fundamentals & Async Programming',
        },
        {
          week: 'Week 2',
          content: 'Express.js & RESTful API Design',
        },
        {
          week: 'Week 3',
          content: 'Postgresql Integration & Advanced Patterns',
        },
        {
          week: 'Week 4',
          content: 'Authentication, Deployment & Final Project',
        },
      ],
    },
    {
      id: 3,
      title: 'Full Stack Development',
      description:
        'Comprehensive full-stack JavaScript development covering frontend, backend, and deployment.',
      fullDescription: `10-Week Intensive Full-Stack JavaScript Development Program.
  
  Key Topics:
  • Modern JavaScript & development tools
  • React.js with hooks and patterns
  • Node.js & Express.js backend
  • Postgres database integration
  • Authentication & security
  • Testing & deployment
  • Professional development practices
  
  Learning Outcomes:
  1. Build modern React applications
  2. Develop REST APIs with Node.js/Express
  3. Work with Postgres and Prisma
  4. Implement authentication and security
  5. Deploy full-stack applications
  6. Write clean, tested code
  7. Work with modern development tools`,
      duration: '10 weeks',
      schedule: '30 hours/week',
      prerequisites: [
        'Programming fundamentals',
        'Basic web development knowledge',
        'JavaScript basics',
        'Strong problem-solving skills',
      ],
      price: '1799',
      image: 'https://images.unsplash.com/photo-1571171637578-41bc2dd41cd2?w=800',
      curriculum: [
        {
          week: 'Week 1-2',
          content: 'JavaScript Foundations & React Basics',
        },
        {
          week: 'Week 3-4',
          content: 'Advanced Frontend & Backend Fundamentals',
        },
        {
          week: 'Week 5-6',
          content: 'Database Integration & Full-Stack Features',
        },
        {
          week: 'Week 7-8',
          content: 'Testing, Deployment & Best Practices',
        },
        {
          week: 'Week 9-10',
          content: 'Capstone Project Development',
        },
      ],
    },
  ];

  const program = programs.find(p => p.id === parseInt(id));

  if (!program) {
    return <div>Program not found</div>;
  }

  return (
    <div className={styles.programContainer}>
      <section className={styles.hero}>
        <img src={program.image} alt={program.title} className={styles.heroImage} />
        <div className={styles.heroContent}>
          <Link to="/programs" className={styles.backButton}>
            <svg className={styles.backIcon} width="24" height="24" viewBox="0 0 24 24">
              <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
            </svg>
            Back to Programs
          </Link>
          <h1 className={styles.title}>{program.title}</h1>
          <div className={styles.metaInfo}>
            <span className={styles.metaItem}>
              <svg className={styles.metaIcon} width="20" height="20" viewBox="0 0 24 24">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z" />
              </svg>
              {program.duration}
            </span>
            <span className={styles.metaItem}>{program.price}</span>
          </div>
        </div>
      </section>

      <div className={styles.content}>
        <div className={styles.mainContent}>
          <div className={styles.description}>{formatDescription(program.fullDescription)}</div>

          <div className={styles.curriculum}>
            <h2 className={styles.curriculumTitle}>Program Curriculum</h2>
            <div className={styles.moduleList}>
              {program.curriculum.map((module, index) => (
                <div key={index} className={styles.module}>
                  <h3 className={styles.moduleWeek}>{module.week}</h3>
                  <p className={styles.moduleContent}>{module.content}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
        <aside className={styles.sidebar}>
          <div className={styles.infoCard}>
            <h3 className={styles.infoTitle}>Prerequisites</h3>
            <ul className={styles.infoList}>
              {program.prerequisites.map((prereq, index) => (
                <li key={index} className={styles.infoItem}>
                  <svg className={styles.infoIcon} width="16" height="16" viewBox="0 0 24 24">
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                  </svg>
                  {prereq}
                </li>
              ))}
            </ul>
            <br />
            <PriceCalculator
              basePrice={parseInt(program.price.replace('₹', ''))}
              programTitle={program.title}
            />
          </div>
        </aside>
      </div>
    </div>
  );
}
